import { useState } from 'react';
import { ImageIcon } from 'lucide-react';

/**
 * <img> drop-in replacement that quietly swaps in a themed placeholder when
 * the remote URL fails to load (404, expired CDN, blocked, etc).
 *
 * Why this exists: third-party providers (Luma, fal.ai, Unsplash hot-link)
 * occasionally return stale URLs that survived in our DB. Instead of showing
 * a browser-rendered "broken image" glyph, we show a themed gradient with the
 * prompt/alt text on top so the page still looks intentional.
 *
 * Props:
 *  - src (string): the primary image URL.
 *  - alt (string): used as fallback text overlay too.
 *  - prompt (string, optional): preferred text to show in the fallback. Falls
 *    back to `alt`.
 *  - className (string): applied to the wrapper (full-bleed by default).
 *  - imgClassName (string): applied to the inner <img>.
 *  - testId (string).
 */
export default function SafeImage({
  src,
  alt = '',
  prompt,
  className = '',
  imgClassName = 'w-full h-full object-cover',
  testId,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  const overlayText = (prompt && prompt.trim()) || alt || '';

  if (errored || !src) {
    return (
      <div
        data-testid={testId ? `${testId}-fallback` : undefined}
        className={`relative flex items-center justify-center bg-gradient-to-br from-[#1C4749]/40 via-[#101F1D] to-[#0A1514] text-white/80 ${className || 'w-full h-full'}`}
      >
        <ImageIcon className="absolute top-3 left-3 w-4 h-4 text-white/30" />
        <span className="px-6 text-center text-[12px] sm:text-sm leading-relaxed text-white/70 line-clamp-3 italic">
          {overlayText ? `\u201C${overlayText}\u201D` : 'Image unavailable'}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      data-testid={testId}
      onError={() => setErrored(true)}
      loading="lazy"
      className={`${className} ${imgClassName}`.trim()}
      {...rest}
    />
  );
}
